import { useMemo, useState } from "react";
import {
	Alert,
	Box,
	Button,
	Chip,
	IconButton,
	Paper,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
	EventRejectionReason,
	EVENT_REASON_CODE_PREFIX,
	RejectionReasonTier,
} from "../../domain/rejectionReason.interface";
import { accountRejectionReasonService } from "../../services/accountRejectionReason.service";

/**
 * Rejection reasons on an event — SIMS-8490, frame `3a`.
 *
 * The block is APPENDED to the existing Events form as its own card (note 1:
 * "Appended, not restructured"), replacing the old free-text "Rejection message"
 * field. Everything above it configures how a submission is judged.
 *
 * 🔴 Opt-in, never inherited. An event that has selected nothing offers only the
 * general-purpose reason (AC1, D-RR-5) — which is why this list can legitimately
 * be empty, and why an empty list is a complete state rather than a missing one.
 */

// 🔴 These MOVED to `domain/rejectionReason.interface` on 2026-09-18. They are
// re-exported here so existing importers keep working, but the domain file is
// where they live: exporting them from a component made `domain/event.interface`
// import from a view, which inverts the dependency direction.
export {
	RejectionReasonTier,
	EVENT_REASON_CODE_PREFIX,
} from "../../domain/rejectionReason.interface";
export type { EventRejectionReason } from "../../domain/rejectionReason.interface";

interface Props {
	reasons: EventRejectionReason[];
	/** What this event's validation type may add — already filtered by the caller. */
	available: EventRejectionReason[];
	onChange: (reasons: EventRejectionReason[]) => void;
	/**
	 * This event's id, when it has one — SIMS-8498 AC4.
	 *
	 * 🔴 Absent while creating. Promotion is a server call against a saved
	 * event, so the control is not offered until the event exists; offering it
	 * on an unsaved form would promise something the API cannot do.
	 */
	eventId?: string | null;
	/** Told after a successful promotion, so the page can refresh its lists. */
	onPromoted?: () => void;
}

/** AC10 — provenance, derived from where a reason came from and never authored. */
const TIER_LABEL: Record<RejectionReasonTier, string> = {
	[RejectionReasonTier.PLATFORM]: "Platform",
	[RejectionReasonTier.ACCOUNT]: "Account",
	[RejectionReasonTier.EVENT]: "This event",
};

/**
 * AC10's second clause — the destructive action says WHICH, because the two have
 * different consequences: a platform or account reason can be re-added tomorrow,
 * an event-only one is gone.
 */
const removeLabel = (tier: RejectionReasonTier): string =>
	tier === RejectionReasonTier.EVENT
		? "Delete this reason — it exists only here and cannot be re-added"
		: "Remove from this event — it can be added again later";

const emptyDraft = (): EventRejectionReason => ({
	id: "",
	title: "",
	description: "",
	participantCopy: "",
	suggestedFix: "",
	requiresNote: false,
	tier: RejectionReasonTier.EVENT,
});

/** AC5 — issued with a fixed prefix so it can never be mistaken for a platform code. */
const codeFor = (title: string): string =>
	EVENT_REASON_CODE_PREFIX +
	title
		.toUpperCase()
		.replace(/[^A-Z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "")
		.slice(0, 60);

export const RejectionReasonsSection = ({
	reasons,
	available,
	onChange,
	eventId,
	onPromoted,
}: Props) => {
	const [draft, setDraft] = useState<EventRejectionReason | null>(null);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [wording, setWording] = useState("");
	const [promoting, setPromoting] = useState<string | null>(null);
	const [promoteError, setPromoteError] = useState<string | null>(null);

	/**
	 * AC4 — promote an event-only reason to the account.
	 *
	 * 🔴 Initiated HERE, where the reason already lives, rather than from the
	 * account screen (PO ruling). Listing every event's private reasons on that
	 * screen would need a cross-collection scan no endpoint provides, and AC1
	 * only ever asked for platform and account rows there.
	 *
	 * The row's tier is updated locally to match what the server did, so the
	 * badge stops reading "This event" without waiting for a refetch. The code
	 * does NOT change — it records where the reason was born.
	 */
	const promote = async (reason: EventRejectionReason): Promise<void> => {
		if (!eventId) return;
		setPromoting(reason.id);
		setPromoteError(null);
		try {
			await accountRejectionReasonService.promoteFromEvent(eventId, reason.id);
			onChange(
				reasons.map((r) =>
					r.id === reason.id ? { ...r, tier: RejectionReasonTier.ACCOUNT } : r,
				),
			);
			onPromoted?.();
		} catch (err) {
			// The server refuses a collision by name — two events can mint the
			// same code from the same title — so its sentence is worth showing
			// rather than a generic failure.
			const message =
				(err as { message?: string })?.message ??
				"Could not promote that reason.";
			setPromoteError(message);
		} finally {
			setPromoting(null);
		}
	};

	const selectedIds = useMemo(() => reasons.map((r) => r.id), [reasons]);
	const addable = available.filter((r) => !selectedIds.includes(r.id));

	const move = (from: number, to: number): void => {
		if (to < 0 || to >= reasons.length) return;
		const next = [...reasons];
		const [row] = next.splice(from, 1);
		next.splice(to, 0, row);
		onChange(next);
	};

	const draftErrors = draft
		? {
				title: draft.title.trim() ? "" : "An internal label is required",
				participantCopy: draft.participantCopy.trim()
					? ""
					: "Participant wording is required",
			}
		: { title: "", participantCopy: "" };
	const draftInvalid = Boolean(
		draftErrors.title || draftErrors.participantCopy,
	);

	return (
		<Paper variant="outlined" sx={{ p: 2 }}>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				sx={{ mb: 0.5 }}
			>
				<Typography variant="subtitle1">Rejection reasons</Typography>
				<Chip label="NEW" size="small" color="primary" />
			</Stack>
			<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
				The reasons a coordinator or the agent may choose when refusing a
				submission for this event.
			</Typography>

			{promoteError && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{promoteError}
				</Alert>
			)}

			<Stack spacing={1}>
				{reasons.map((reason, index) => {
					const overridden =
						reason.originalParticipantCopy !== undefined &&
						reason.originalParticipantCopy !== reason.participantCopy;

					return (
						<Paper key={reason.id} variant="outlined" sx={{ p: 1.5 }}>
							<Stack direction="row" spacing={1} alignItems="flex-start">
								{/* AC3. 🔴 Deviation from frame 3a, which draws no drag
								    handle here — built to the Measurements block's idiom on
								    the same screen, because AC3 requires ordering. */}
								<Stack sx={{ pt: 0.5 }}>
									{/* 🔴 Decorative, and hidden from assistive tech. Raised in
									    review: there is no drag handler anywhere in this
									    component — reordering is done by the labelled Move up /
									    Move down buttons beside it. Announcing a nameless icon
									    that implies dragging would promise an interaction that
									    does not exist. */}
									<DragIndicatorIcon
										fontSize="small"
										color="disabled"
										aria-hidden="true"
									/>
									<IconButton
										size="small"
										aria-label={`Move ${reason.title} up`}
										disabled={index === 0}
										onClick={() => move(index, index - 1)}
									>
										▲
									</IconButton>
									<IconButton
										size="small"
										aria-label={`Move ${reason.title} down`}
										disabled={index === reasons.length - 1}
										onClick={() => move(index, index + 1)}
									>
										▼
									</IconButton>
								</Stack>

								<Box sx={{ flexGrow: 1 }}>
									<Typography variant="subtitle2">{reason.title}</Typography>
									<Typography variant="body2" color="text.secondary">
										{[
											`${TIER_LABEL[reason.tier]} reason`,
											reason.requiresNote ? "requires an internal note" : "",
										]
											.filter(Boolean)
											.join(" · ")}
									</Typography>

									{editingId === reason.id ? (
										<Stack spacing={1} sx={{ mt: 1 }}>
											<TextField
												label="Participant wording for this event"
												size="small"
												fullWidth
												multiline
												value={wording}
												onChange={(e) => setWording(e.target.value)}
											/>
											{/* AC4 — the original stays visible beside the override
											    and can be restored at any time. */}
											{reason.originalParticipantCopy && (
												<Typography variant="caption" color="text.secondary">
													Original: “{reason.originalParticipantCopy}”
												</Typography>
											)}
											<Stack direction="row" spacing={1}>
												<Button
													size="small"
													variant="contained"
													disabled={!wording.trim()}
													onClick={() => {
														onChange(
															reasons.map((r) =>
																r.id === reason.id
																	? {
																			...r,
																			originalParticipantCopy:
																				r.originalParticipantCopy ??
																				r.participantCopy,
																			participantCopy: wording.trim(),
																		}
																	: r,
															),
														);
														setEditingId(null);
													}}
												>
													Save wording
												</Button>
												<Button size="small" onClick={() => setEditingId(null)}>
													Cancel
												</Button>
												{overridden && (
													<Button
														size="small"
														startIcon={<RestartAltIcon />}
														onClick={() => {
															onChange(
																reasons.map((r) =>
																	r.id === reason.id
																		? {
																				...r,
																				participantCopy:
																					r.originalParticipantCopy ??
																					r.participantCopy,
																			}
																		: r,
																),
															);
															setEditingId(null);
														}}
													>
														Restore original
													</Button>
												)}
											</Stack>
										</Stack>
									) : (
										<Typography
											variant="body2"
											sx={{ fontStyle: "italic", mt: 0.5 }}
										>
											The participant will be told: “{reason.participantCopy}”
											{overridden && " (reworded for this event)"}
										</Typography>
									)}
								</Box>

								<Chip label={TIER_LABEL[reason.tier]} size="small" />

								{/* AC4 — promote, offered only on a reason that exists
								    ONLY here and only once the event has been saved.
								    🔴 AC5: there is deliberately no inverse. Nothing on this
								    row turns an account reason back into an event-only one. */}
								{reason.tier === RejectionReasonTier.EVENT && eventId && (
									<Button
										size="small"
										aria-label={`Promote ${reason.title} to the account`}
										disabled={promoting === reason.id}
										onClick={() => void promote(reason)}
									>
										{promoting === reason.id
											? "Promoting…"
											: "Promote to account"}
									</Button>
								)}

								<IconButton
									size="small"
									aria-label={`Reword ${reason.title}`}
									onClick={() => {
										setEditingId(reason.id);
										setWording(reason.participantCopy);
									}}
								>
									<EditOutlinedIcon fontSize="small" />
								</IconButton>

								{/* 🔴 AC9 INVERTED 2026-09-18 — removal is never blocked by
								    the agent now. The agent holds no reason: it is offered
								    this list at validation time and picks from it
								    (SIMS-8215 AC2), so from the next submission onward it
								    is simply offered what remains. */}
								<Tooltip title={removeLabel(reason.tier)}>
									<span>
										<IconButton
											size="small"
											aria-label={`Remove ${reason.title}`}
											onClick={() =>
												onChange(reasons.filter((r) => r.id !== reason.id))
											}
										>
											<DeleteOutlineIcon fontSize="small" />
										</IconButton>
									</span>
								</Tooltip>
							</Stack>
						</Paper>
					);
				})}

				{/* AC8 — not a row like the others. The frame puts the floor on a flat
				    read-only surface with a lock, so "offers no removal action" is
				    expressed by it not being editable at all. */}
				<Paper variant="outlined" sx={{ p: 1.5, bgcolor: "action.hover" }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<LockOutlinedIcon fontSize="small" color="disabled" />
						<Box sx={{ flexGrow: 1 }}>
							<Typography variant="subtitle2">Other</Typography>
							<Typography variant="body2" color="text.secondary">
								Always offered and cannot be removed. Requires a written reason.
							</Typography>
						</Box>
						<Chip label="Platform" size="small" />
					</Stack>
				</Paper>
			</Stack>

			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="flex-start"
				sx={{ mt: 2 }}
			>
				<Stack direction="row" spacing={1} flexWrap="wrap">
					{addable.map((reason) => (
						<Button
							key={reason.id}
							size="small"
							variant="outlined"
							onClick={() => onChange([...reasons, reason])}
						>
							+ {reason.title}
						</Button>
					))}
					<Button
						size="small"
						variant="outlined"
						onClick={() => setDraft(emptyDraft())}
					>
						+ ADD A REASON
					</Button>
				</Stack>
				{/* Frame 3a states this rule on screen, because it is otherwise
				    invisible and expensive to get wrong.

				    🔴 SIMS-9135 — the freeze point is the DECISION, not activation.
				    This caption used to say "fixed when the treatment plan is
				    activated", which was true of an earlier cut that snapshotted the
				    catalogue onto `currentTreatmentPlan`. That snapshot was dropped:
				    a plan spans many events, so two events wording the same reason
				    differently collided in one id-keyed list. `recordRejectionReason`
				    now copies the wording onto each rejection as it is recorded.

				    The old text promised a safety boundary at activation that does
				    not exist, on the one screen where participant-facing copy is
				    written — a designer would reasonably read it as "rewording an
				    active plan is inert", when the next rejection sends the new
				    wording. Both halves of the real rule are stated, because the
				    reassuring half alone is what made the wrong half believable. */}
				<Typography
					variant="caption"
					color="text.secondary"
					sx={{ maxWidth: 320, textAlign: "right" }}
				>
					Editing a reason changes what future rejections send. Every rejection
					already recorded keeps the wording it was sent.
				</Typography>
			</Stack>

			{/* AC5 + AC6 — not drawn on the frame; built from the ACs. */}
			{draft && (
				<Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						A reason for this event only
					</Typography>
					<Stack spacing={1.5}>
						<TextField
							label="Internal label"
							size="small"
							required
							fullWidth
							value={draft.title}
							onChange={(e) => setDraft({ ...draft, title: e.target.value })}
							error={Boolean(draftErrors.title)}
							helperText={draftErrors.title || " "}
						/>
						<TextField
							label="When to pick it"
							size="small"
							fullWidth
							value={draft.description}
							onChange={(e) =>
								setDraft({ ...draft, description: e.target.value })
							}
						/>
						<TextField
							label="Participant wording"
							size="small"
							required
							fullWidth
							multiline
							value={draft.participantCopy}
							onChange={(e) =>
								setDraft({ ...draft, participantCopy: e.target.value })
							}
							error={Boolean(draftErrors.participantCopy)}
							helperText={draftErrors.participantCopy || " "}
						/>
						<TextField
							label="Suggested fix"
							size="small"
							fullWidth
							value={draft.suggestedFix}
							onChange={(e) =>
								setDraft({ ...draft, suggestedFix: e.target.value })
							}
						/>
						<Alert severity="info">
							This reason will exist on this event only. Its code is issued with
							the {EVENT_REASON_CODE_PREFIX} prefix so it can never be confused
							with a platform reason of the same name.
						</Alert>
						<Stack direction="row" spacing={1}>
							<Button
								variant="contained"
								size="small"
								disabled={draftInvalid}
								onClick={() => {
									onChange([
										...reasons,
										{ ...draft, id: codeFor(draft.title) },
									]);
									setDraft(null);
								}}
							>
								Add to this event
							</Button>
							<Button size="small" onClick={() => setDraft(null)}>
								Cancel
							</Button>
						</Stack>
					</Stack>
				</Paper>
			)}
		</Paper>
	);
};
